// Renders a Figma-exported SVG inline so `currentColor` (stroke/fill) follows
// the surrounding text color. SVGs are imported as raw strings (Vite `?raw`).
import { useMemo } from 'react';

// Action-row + common icons (blue stroke, recolored via currentColor)
import receive from '../assets/icons/receive.svg?raw';
import send from '../assets/icons/send.svg?raw';
import buy from '../assets/icons/buy.svg?raw';
import qr from '../assets/icons/qr.svg?raw';
// Tab-bar glyphs (bold fill)
import navWallet from '../assets/icons/nav-wallet.svg?raw';
import navCards from '../assets/icons/nav-cards.svg?raw';
import qrFill from '../assets/icons/qr-fill.svg?raw';
import arrowRight from '../assets/icons/arrow-right.svg?raw';
import copy from '../assets/icons/copy.svg?raw';
import visaChevron from '../assets/icons/visa-chevron.svg?raw';
import visaLogo from '../assets/icons/visa.svg?raw';
import shieldSecurity from '../assets/icons/shield-security.svg?raw';
import edit2 from '../assets/icons/edit-2.svg?raw';
import trash from '../assets/icons/trash.svg?raw';
// Profile screen row icons (vuesax bold, white glyph on colored tiles)
import pfCard from '../assets/icons/pf-card.svg?raw';
import pfEmail from '../assets/icons/pf-email.svg?raw';
import pfPin from '../assets/icons/pf-pin.svg?raw';
import pfFaceId from '../assets/icons/pf-faceid.svg?raw';
import pfSupport from '../assets/icons/pf-support.svg?raw';
import pfPolicy from '../assets/icons/pf-policy.svg?raw';
import pfFaq from '../assets/icons/pf-faq.svg?raw';
import pfSeed from '../assets/icons/pf-seed.svg?raw';
import pfReceipt from '../assets/icons/pf-receipt.svg?raw';
import pfSms from '../assets/icons/pf-sms.svg?raw';
import pfLock from '../assets/icons/pf-lock.svg?raw';
import pfSun from '../assets/icons/pf-sun.svg?raw';
import pfSetting from '../assets/icons/pf-setting.svg?raw';
import pfCheck from '../assets/icons/pf-check.svg?raw';
// Coins (fixed brand colors)
import usdt from '../assets/coins/usdt.svg?raw';
import trx from '../assets/coins/trx.svg?raw';

const RAW = {
  receive,
  send,
  buy,
  add: buy, // vuesax/linear/add — same glyph as Buy
  qr,
  navWallet,
  navCards,
  qrFill,
  arrowRight,
  copy,
  visaChevron,
  shieldSecurity,
  edit2,
  trash,
  pfCard,
  pfEmail,
  pfPin,
  pfFaceId,
  pfSupport,
  pfPolicy,
  pfFaq,
  pfSeed,
  pfReceipt,
  pfSms,
  pfLock,
  pfSun,
  pfSetting,
  pfCheck,
  usdt,
  trx,
} as const;

export type FigIconName = keyof typeof RAW;

/** Strip the root <svg> width/height so our size prop wins; keep the viewBox. */
function sized(raw: string, size: number): string {
  return raw
    .replace(/<svg([^>]*?)\swidth="[^"]*"/, '<svg$1')
    .replace(/<svg([^>]*?)\sheight="[^"]*"/, '<svg$1')
    .replace(/<svg /, `<svg width="${size}" height="${size}" `);
}

export function FigIcon({
  name,
  size = 24,
  className,
}: {
  name: FigIconName;
  size?: number;
  className?: string;
}) {
  const html = useMemo(() => sized(RAW[name], size), [name, size]);
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', lineHeight: 0 }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** The VISA wordmark (Figma asset) — wide aspect (81:27), so it renders at a
 *  fixed height with proportional width. Color follows `currentColor`. */
export function VisaLogo({ height = 8, className }: { height?: number; className?: string }) {
  const html = useMemo(
    () =>
      visaLogo
        .replace(/<svg([^>]*?)\swidth="[^"]*"/, '<svg$1')
        .replace(/<svg([^>]*?)\sheight="[^"]*"/, '<svg$1')
        .replace(/<svg /, `<svg height="${height}" width="${(height * 81) / 27}" `),
    [height],
  );
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', lineHeight: 0 }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
